import { findLastIndex } from 'lodash-es';

import { SENDER_TYPES } from './senderTypes';

// Leave a little breathing room above a bot answer when we pin it to the top of
// the scroll region so the first line isn't flush against the edge.
export const MESSAGE_TOP_SCROLL_OFFSET_PX = 12;

const SCROLL_TO_BOTTOM = { type: 'bottom' };

/**
 * A "response" is substantive bot content the user reads top-to-bottom. Feedback
 * survey prompts and acknowledgements are excluded so they never steal scroll
 * position from the answer above them.
 * @param {{ sender?: string, isFeedback?: boolean, feedbackAcknowledgementTargetMessageId?: string }} [message]
 * @returns {boolean}
 */
export const isBotResponseMessage = message =>
  message?.sender === SENDER_TYPES.BOT &&
  !message?.isFeedback &&
  !message?.feedbackAcknowledgementTargetMessageId;

/**
 * Index of the most recent substantive bot answer, or -1 when there isn't one.
 * @param {Array} messages
 * @returns {number}
 */
export const findLatestBotResponseIndex = messages =>
  findLastIndex(messages, isBotResponseMessage);

/**
 * @typedef {{ type: 'bottom' } | { type: 'response', index: number }} ChatScrollAction
 */

/**
 * Decide how the transcript should scroll after a render, without touching the
 * DOM so the logic stays unit-testable. Returns `null` when the scroll position
 * should be left untouched.
 * @param {{ errorMessage?: string, messages: Array, previousMessageCount: number }} params
 * @returns {ChatScrollAction | null}
 */
export const resolveChatScroll = ({
  errorMessage,
  messages,
  previousMessageCount,
}) => {
  const lastMessage = messages[messages.length - 1];

  // An error banner or an empty/initial transcript should sit at the bottom.
  if (errorMessage || !lastMessage) {
    return SCROLL_TO_BOTTOM;
  }

  // The user just sent something: keep their message and the typing indicator
  // in view.
  if (lastMessage.sender === SENDER_TYPES.USER) {
    return SCROLL_TO_BOTTOM;
  }

  // Nothing new arrived (feedback-pending toggle, an acknowledgement replacing a
  // prompt in place, or a typing-state change): leave the scroll untouched.
  if (messages.length <= previousMessageCount) {
    return null;
  }

  // New bot content arrived (an answer and/or a feedback prompt). Anchor to the
  // most recent substantive answer, leaving the pin-vs-bottom choice to layout.
  const index = findLatestBotResponseIndex(messages);
  return index >= 0 ? { type: 'response', index } : SCROLL_TO_BOTTOM;
};

// Set the region's vertical scroll, falling back to scrollTop for environments
// (e.g. older jsdom) without scrollTo.
const setRegionScrollTop = (messageRegion, top) => {
  if (typeof messageRegion.scrollTo === 'function') {
    messageRegion.scrollTo({ top });
  } else {
    // Mutating the live DOM node is the intent here, not a stray param reassign.
    // eslint-disable-next-line no-param-reassign
    messageRegion.scrollTop = top;
  }
};

// Scroll the message region to its bottom, preferring the end anchor's
// scrollIntoView so the trailing typing indicator stays in view.
const scrollMessageRegionToBottom = (messageRegion, messageEnd) => {
  if (messageEnd && typeof messageEnd.scrollIntoView === 'function') {
    messageEnd.scrollIntoView({ block: 'end' });
    return;
  }

  setRegionScrollTop(messageRegion, messageRegion.scrollHeight);
};

// Anchor the view to a bot answer. We only pin its first line to the top of the
// scroll region when the answer (plus anything after it, e.g. a feedback prompt)
// is too tall to keep that first line visible while scrolled to the bottom.
// Otherwise we just scroll all the way down so the trailing prompt is in view.
const scrollMessageRegionToResponse = (messageRegion, index, messageEnd) => {
  const target = messageRegion.querySelector(
    `[data-chat-message-index="${index}"]`,
  );

  if (!target || typeof target.getBoundingClientRect !== 'function') {
    scrollMessageRegionToBottom(messageRegion, messageEnd);
    return;
  }

  const regionTop = messageRegion.getBoundingClientRect().top;
  const targetTop = target.getBoundingClientRect().top;
  // Content-space Y coordinate of the answer's top edge.
  const responseOffset = messageRegion.scrollTop + (targetTop - regionTop);
  const maxScrollTop = messageRegion.scrollHeight - messageRegion.clientHeight;

  // When scrolled to the bottom the viewport starts at `maxScrollTop`, so the
  // answer's first line is still visible if it sits at/below there.
  if (responseOffset >= maxScrollTop) {
    scrollMessageRegionToBottom(messageRegion, messageEnd);
    return;
  }

  setRegionScrollTop(
    messageRegion,
    Math.max(0, responseOffset - MESSAGE_TOP_SCROLL_OFFSET_PX),
  );
};

/**
 * Execute a resolved scroll action against the live DOM nodes.
 * @param {ChatScrollAction | null} scrollAction
 * @param {{ messageRegion: HTMLElement, messageEnd?: HTMLElement }} nodes
 */
export const applyChatScroll = (
  scrollAction,
  { messageRegion, messageEnd },
) => {
  if (!scrollAction || !messageRegion) {
    return;
  }

  if (scrollAction.type === 'response') {
    scrollMessageRegionToResponse(
      messageRegion,
      scrollAction.index,
      messageEnd,
    );
  } else {
    scrollMessageRegionToBottom(messageRegion, messageEnd);
  }
};
